// SETTINGS V2 — Team page pure utils (SETTINGS_V2_SP6 §5.5 / §5.6).
//
// getTimeRemainingV2 ports the legacy useEmployeeTime math VERBATIM (hook lines
// 4-31) but localizes the suffix (binds settings:team.remaining — the ticket
// §4.5 'remaining' FIX+FLAG); the legacy hook stays untouched. formatDate /
// formatDateTime replace the legacy Moment formatter mechanically (DD.MM.YYYY /
// DD.MM.YYYY HH:mm, zero-padded, from new Date(d) local time — byte-identical to
// the Moment output for valid dates). Pure functions, no side effects.
import type { TFunction } from "i18next";

export interface TimeRemainingV2 {
  expired: boolean;
  text: string;
}

/** Legacy math verbatim; only the hardcoded "remaining" suffix becomes i18n. */
export const getTimeRemainingV2 = (
  expirationDate: Date | undefined,
  t: TFunction
): TimeRemainingV2 | null => {
  if (!expirationDate) {
    return null;
  }

  const now = new Date();
  const expiration = new Date(expirationDate);
  const diffMs = expiration.getTime() - now.getTime();

  if (diffMs <= 0) {
    return { expired: true, text: "" };
  }

  const totalMinutes = Math.floor(diffMs / (1000 * 60));
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;

  let timeText = "";
  if (hours > 0) {
    timeText += `${hours}h `;
  }
  if (minutes > 0 || hours === 0) {
    timeText += `${minutes}m `;
  }

  // ticket §4.5 fix: localized suffix instead of the hook's hardcoded "remaining".
  return {
    expired: false,
    text: t("settings:team.remaining", { time: timeText.trim() }) as string,
  };
};

const pad = (n: number): string => String(n).padStart(2, "0");

/** DD.MM.YYYY (Moment "DD.MM.YYYY" equivalent for valid dates). */
export const formatDate = (d: Date | string): string => {
  const date = new Date(d);
  return `${pad(date.getDate())}.${pad(date.getMonth() + 1)}.${date.getFullYear()}`;
};

/** DD.MM.YYYY HH:mm (Moment "DD.MM.YYYY HH:mm" equivalent for valid dates). */
export const formatDateTime = (d: Date | string): string => {
  const date = new Date(d);
  return `${formatDate(date)} ${pad(date.getHours())}:${pad(date.getMinutes())}`;
};
